// frontend/src/components/engineer/pages/ViewTimesheetsPage.tsx - WITH FLEXIBLE DATE PICKER
import React, { useState, useCallback, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  XMarkIcon,
  CalendarDaysIcon,
  UserIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { AuthUser } from '../../../services/auth.service';
import {
  timesheetService,
  TimesheetEmployee,
  TimesheetData,
  SalaryPeriod
} from '../../../services/timesheet.service';
import styles from './CreateOvertimePage.module.css';

interface ViewTimesheetsPageProps {
  user?: AuthUser;
  onNavigate?: (page: string) => void;
}

const ViewTimesheetsPage: React.FC<ViewTimesheetsPageProps> = ({ user, onNavigate }) => {
  // Search State
  const [searchPin, setSearchPin] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Employee State
  const [selectedEmployees, setSelectedEmployees] = useState<TimesheetEmployee[]>([]);
  const [searchResults, setSearchResults] = useState<TimesheetEmployee | null>(null);

  // Date Selection State
  const [dateSelectionMode, setDateSelectionMode] = useState<'quick' | 'custom'>('quick');
  const [availablePeriods] = useState<SalaryPeriod[]>(
    timesheetService.getAvailableSalaryPeriods(6)
  );
  const [selectedPeriod, setSelectedPeriod] = useState<SalaryPeriod>(availablePeriods[0]);
  
  // Custom Date Range State
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [dateError, setDateError] = useState('');

  // Timesheet State
  const [generatedTimesheets, setGeneratedTimesheets] = useState<TimesheetData[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showTimesheetView, setShowTimesheetView] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Ref for print
  const timesheetPrintRef = useRef<HTMLDivElement>(null);

  /**
   * Search employee by PIN
   */
  const handleSearch = useCallback(async () => {
    if (!searchPin.trim()) {
      setSearchError('Please enter a PIN/AUX number');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    setSearchResults(null);

    try {
      const employee = await timesheetService.searchEmployeeByPin(searchPin.trim());

      if (employee) {
        setSearchResults(employee);
      } else {
        setSearchError('Employee not found. Please check the PIN/AUX number.');
      }
    } catch (error) {
      console.error('Error searching employee:', error);
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [searchPin]);

  /**
   * Add employee to selection
   */
  const handleAddEmployee = useCallback(() => {
    if (searchResults && !selectedEmployees.find(emp => emp.id === searchResults.id)) {
      setSelectedEmployees(prev => [...prev, searchResults]);
      setSearchResults(null);
      setSearchPin('');
      setSearchError('');
    }
  }, [searchResults, selectedEmployees]);

  /**
   * Remove employee from selection
   */
  const handleRemoveEmployee = useCallback((employeeId: string) => {
    setSelectedEmployees(prev => prev.filter(emp => emp.id !== employeeId));
  }, []);

  /**
   * Validate custom date range
   */
  const validateCustomDates = useCallback((): boolean => {
    if (!customStartDate || !customEndDate) {
      setDateError('Please select both start and end dates');
      return false;
    }

    const start = new Date(customStartDate);
    const end = new Date(customEndDate);

    if (start > end) {
      setDateError('Start date must be before end date');
      return false;
    }

    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 60) {
      setDateError('Date range cannot exceed 60 days');
      return false;
    }

    setDateError('');
    return true;
  }, [customStartDate, customEndDate]);

  /**
   * Get selected date range
   */
  const getSelectedDateRange = useCallback((): { startDate: Date; endDate: Date; label: string } => {
    if (dateSelectionMode === 'custom') {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      
      const formatDate = (date: Date) => {
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      return {
        startDate: start,
        endDate: end,
        label: `${formatDate(start)} to ${formatDate(end)}`
      };
    } else {
      return {
        startDate: selectedPeriod.startDate,
        endDate: selectedPeriod.endDate,
        label: selectedPeriod.label
      };
    }
  }, [dateSelectionMode, customStartDate, customEndDate, selectedPeriod]);

  /**
   * Generate timesheets
   */
  const handleGenerateTimesheets = useCallback(async () => {
    if (selectedEmployees.length === 0) {
      alert('Please add at least one employee');
      return;
    }

    // Validate dates for custom mode
    if (dateSelectionMode === 'custom' && !validateCustomDates()) {
      return;
    }

    setIsGenerating(true);

    try {
      const dateRange = getSelectedDateRange();
      const timesheets: TimesheetData[] = [];

      for (const employee of selectedEmployees) {
        const timesheet = await timesheetService.generateTimesheet(
          employee,
          dateRange.startDate,
          dateRange.endDate
        );
        timesheets.push(timesheet);
      }

      setGeneratedTimesheets(timesheets);
      setShowTimesheetView(true);

      console.log(`✅ Generated ${timesheets.length} timesheets for period: ${dateRange.label}`);
    } catch (error) {
      console.error('Error generating timesheets:', error);
      alert('Failed to generate timesheets. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [selectedEmployees, dateSelectionMode, validateCustomDates, getSelectedDateRange]);

  /**
   * Export all timesheets to Excel
   */
  const handleExportAll = useCallback(async () => {
    if (generatedTimesheets.length === 0) return;

    setIsExporting(true);

    try {
      for (const timesheet of generatedTimesheets) {
        await timesheetService.exportTimesheetToExcel(timesheet);
      }

      alert(`Successfully exported ${generatedTimesheets.length} timesheet(s)`);
    } catch (error) {
      console.error('Error exporting timesheets:', error);
      alert('Failed to export timesheets');
    } finally {
      setIsExporting(false);
    }
  }, [generatedTimesheets]);

  /**
   * Print timesheets
   */
  const handlePrint = useCallback(() => {
    if (!timesheetPrintRef.current) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = timesheetPrintRef.current.innerHTML;
    const dateRange = getSelectedDateRange();

    const printHTML = `
      <html>
        <head>
          <title>Timesheets - ${dateRange.label}</title>
          <style>
            @page { margin: 0.5in; size: A4 landscape; }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              font-size: 10px;
            }
            .timesheet-container { page-break-after: always; margin-bottom: 40px; }
            .timesheet-header { text-align: center; margin-bottom: 20px; border-bottom: 3px solid #c29e5d; padding-bottom: 15px; }
            .company-title { font-size: 18px; font-weight: bold; color: #c29e5d; margin-bottom: 5px; }
            .timesheet-title { font-size: 14px; font-weight: 600; color: #666; }
            .employee-info { background: #f8f9fa; padding: 15px; margin: 20px 0; border-radius: 8px; }
            .info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
            .info-item { font-size: 10px; }
            .info-label { font-weight: bold; color: #333; }
            .timesheet-table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 9px; }
            .timesheet-table th, .timesheet-table td { border: 1px solid #ddd; padding: 6px 4px; text-align: left; }
            .timesheet-table th { background: #f8f9fa; font-weight: bold; font-size: 9px; }
            .timesheet-table tr:nth-child(even) { background: #fafafa; }
            .no-break { background: #fef3c7; color: #d97706; font-weight: 600; }
            .discrepancy { color: #dc2626; font-weight: 600; }
            .summary-section { background: #f0fdf4; padding: 15px; margin: 20px 0; border-radius: 8px; border: 1px solid #bbf7d0; }
            .summary-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; }
            .summary-item { text-align: center; }
            .summary-value { font-size: 16px; font-weight: bold; color: #059669; }
            .summary-label { font-size: 9px; color: #047857; }
            @media print {
              body { font-size: 9px; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `;

    printWindow.document.write(printHTML);
    printWindow.document.close();

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  }, [getSelectedDateRange]);

  /**
   * Reset and go back
   */
  const handleReset = useCallback(() => {
    setShowTimesheetView(false);
    setGeneratedTimesheets([]);
  }, []);

  return (
    <div className={styles.createOvertimeContainer}>
      <div className={styles.pageWrapper}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>View Timesheets</h1>
          <p className={styles.pageSubtitle}>
            Generate employee timesheets for any date range
          </p>
        </div>

        {!showTimesheetView ? (
          <>
            {/* Employee Search Section */}
            <div className={styles.stepPanel}>
              <div className={styles.stepHeader}>
                <UserIcon className={styles.stepHeaderIcon} />
                <div>
                  <h2 className={styles.stepHeaderTitle}>Search Employee</h2>
                  <p className={styles.stepHeaderSubtitle}>
                    Search by PIN/AUX number to add employees
                  </p>
                </div>
              </div>

              <div className={styles.stepBody}>
                {/* Search Input */}
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '2rem' }}>
                  <div className={styles.inputGroup} style={{ flex: 1 }}>
                    <label htmlFor="searchPin" className={styles.inputLabel}>
                      Employee PIN/AUX Number
                    </label>
                    <input
                      id="searchPin"
                      type="text"
                      value={searchPin}
                      onChange={(e) => setSearchPin(e.target.value)}
                      className={`${styles.textInput} ${searchError ? styles.error : ''}`}
                      placeholder="Enter PIN/AUX number (e.g., 3576, 1289)"
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      disabled={isSearching}
                    />
                    {searchError && (
                      <p className={styles.errorText}>{searchError}</p>
                    )}
                  </div>
                  <button
                    onClick={handleSearch}
                    disabled={isSearching || !searchPin.trim()}
                    className={`${styles.navButton} ${styles.primary}`}
                    style={{ minWidth: '120px' }}
                  >
                    {isSearching ? (
                      <>
                        <div className={styles.submitSpinner}></div>
                        Searching...
                      </>
                    ) : (
                      <>
                        <MagnifyingGlassIcon className={styles.navIcon} />
                        Search
                      </>
                    )}
                  </button>
                </div>

                {/* Search Result */}
                {searchResults && (
                  <div style={{
                    background: 'linear-gradient(135deg, #f0fdf4, #f7fee7)',
                    border: '1px solid #bbf7d0',
                    borderRadius: '1rem',
                    padding: '1.5rem',
                    marginBottom: '2rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 1rem 0', color: '#047857', fontSize: '1.125rem' }}>
                          Employee Found
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>Name</div>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                              {searchResults.name}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>PIN/AUX</div>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                              {searchResults.pin}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>Designation</div>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                              {searchResults.designation}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>Department</div>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                              {searchResults.department}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>Shift</div>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#047857' }}>
                              {searchResults.shiftStart} - {searchResults.shiftEnd}
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleAddEmployee}
                        disabled={selectedEmployees.find(emp => emp.id === searchResults.id) !== undefined}
                        className={`${styles.navButton} ${styles.submit} ${styles.enabled}`}
                        style={{ minWidth: '120px', marginLeft: '1rem' }}
                      >
                        {selectedEmployees.find(emp => emp.id === searchResults.id) ? (
                          <>
                            <CheckCircleIcon className={styles.navIcon} />
                            Added
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className={styles.navIcon} />
                            Add Employee
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Selected Employees */}
                {selectedEmployees.length > 0 && (
                  <div style={{
                    background: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: '1rem',
                    padding: '1.5rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h4 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: '#322821' }}>
                        Selected Employees ({selectedEmployees.length})
                      </h4>
                      <button
                        onClick={() => setSelectedEmployees([])}
                        className={styles.clearAllButton}
                      >
                        Clear All
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {selectedEmployees.map(employee => (
                        <div
                          key={employee.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '1rem',
                            background: '#f9fafb',
                            borderRadius: '0.5rem',
                            border: '1px solid #e5e7eb'
                          }}
                        >
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', flex: 1 }}>
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Name</div>
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                                {employee.name}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>PIN</div>
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                                {employee.pin}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Designation</div>
                              <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                                {employee.designation}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Shift</div>
                              <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                                {employee.shiftStart} - {employee.shiftEnd}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveEmployee(employee.id)}
                            style={{
                              background: '#fee2e2',
                              border: 'none',
                              padding: '0.5rem',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              marginLeft: '1rem'
                            }}
                          >
                            <XMarkIcon style={{ width: '1rem', height: '1rem', color: '#dc2626' }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Date Selection Section */}
            <div className={styles.stepPanel}>
              <div className={styles.stepHeader}>
                <CalendarDaysIcon className={styles.stepHeaderIcon} />
                <div>
                  <h2 className={styles.stepHeaderTitle}>Select Date Range</h2>
                  <p className={styles.stepHeaderSubtitle}>
                    Choose quick salary period or custom date range
                  </p>
                </div>
              </div>

              <div className={styles.stepBody}>
                {/* Mode Toggle */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem' }}>
                  <button
                    onClick={() => setDateSelectionMode('quick')}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      background: dateSelectionMode === 'quick' ? 'linear-gradient(135deg, #c29e5d, #d4b373)' : '#f9fafb',
                      color: dateSelectionMode === 'quick' ? 'white' : '#374151',
                      border: dateSelectionMode === 'quick' ? '2px solid #c29e5d' : '1px solid #e5e7eb',
                      borderRadius: '0.75rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Quick Salary Periods (21st to 20th)
                  </button>
                  <button
                    onClick={() => setDateSelectionMode('custom')}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      background: dateSelectionMode === 'custom' ? 'linear-gradient(135deg, #c29e5d, #d4b373)' : '#f9fafb',
                      color: dateSelectionMode === 'custom' ? 'white' : '#374151',
                      border: dateSelectionMode === 'custom' ? '2px solid #c29e5d' : '1px solid #e5e7eb',
                      borderRadius: '0.75rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Custom Date Range
                  </button>
                </div>

                {/* Quick Period Selection */}
                {dateSelectionMode === 'quick' && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                    {availablePeriods.map((period, index) => (
                      <label
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '1rem',
                          background: selectedPeriod === period ? 'linear-gradient(135deg, #f0fdf4, #f7fee7)' : '#f9fafb',
                          border: selectedPeriod === period ? '2px solid #10b981' : '1px solid #e5e7eb',
                          borderRadius: '0.75rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <input
                          type="radio"
                          checked={selectedPeriod === period}
                          onChange={() => setSelectedPeriod(period)}
                          style={{ marginRight: '0.75rem' }}
                        />
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                            {period.label}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            {index === 0 ? 'Current Period' : `${index} period(s) ago`}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {/* Custom Date Range Selection */}
                {dateSelectionMode === 'custom' && (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1rem' }}>
                      <div className={styles.inputGroup}>
                        <label htmlFor="startDate" className={styles.inputLabel}>
                          Start Date
                        </label>
                        <input
                          id="startDate"
                          type="date"
                          value={customStartDate}
                          onChange={(e) => {
                            setCustomStartDate(e.target.value);
                            setDateError('');
                          }}
                          className={styles.textInput}
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div className={styles.inputGroup}>
                        <label htmlFor="endDate" className={styles.inputLabel}>
                          End Date
                        </label>
                        <input
                          id="endDate"
                          type="date"
                          value={customEndDate}
                          onChange={(e) => {
                            setCustomEndDate(e.target.value);
                            setDateError('');
                          }}
                          className={styles.textInput}
                          max={new Date().toISOString().split('T')[0]}
                          min={customStartDate}
                        />
                      </div>
                    </div>

                    {dateError && (
                      <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                        {dateError}
                      </p>
                    )}

                    {customStartDate && customEndDate && !dateError && (
                      <div style={{
                        background: 'linear-gradient(135deg, #f0fdf4, #f7fee7)',
                        border: '1px solid #bbf7d0',
                        borderRadius: '0.75rem',
                        padding: '1rem',
                        marginTop: '1rem'
                      }}>
                        <div style={{ fontSize: '0.875rem', color: '#047857', fontWeight: '600' }}>
                          Selected Range: {new Date(customStartDate).toLocaleDateString()} - {new Date(customEndDate).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.25rem' }}>
                          {Math.ceil((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} days
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <div className={styles.navigationButtons}>
              <div className={styles.navSpacer} />
              <button
                onClick={handleGenerateTimesheets}
                disabled={
                  selectedEmployees.length === 0 || 
                  isGenerating ||
                  (dateSelectionMode === 'custom' && (!customStartDate || !customEndDate))
                }
                className={`${styles.navButton} ${styles.submit} ${
                  selectedEmployees.length === 0 || 
                  isGenerating ||
                  (dateSelectionMode === 'custom' && (!customStartDate || !customEndDate))
                    ? styles.disabled 
                    : styles.enabled
                }`}
              >
                {isGenerating ? (
                  <>
                    <div className={styles.submitSpinner}></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <DocumentArrowDownIcon className={styles.navIcon} />
                    Generate Timesheets ({selectedEmployees.length})
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Timesheet View */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem',
              padding: '1rem',
              background: 'white',
              borderRadius: '1rem',
              border: '1px solid #e5e7eb'
            }}>
              <div>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', fontWeight: '700', color: '#322821' }}>
                  Generated Timesheets
                </h2>
                <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                  Period: {getSelectedDateRange().label} • {generatedTimesheets.length} employee(s)
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={handleExportAll}
                  disabled={isExporting}
                  className={styles.navButton}
                  style={{
                    background: 'linear-gradient(135deg, #059669, #10b981)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {isExporting ? (
                    <>
                      <div className={styles.submitSpinner}></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <DocumentArrowDownIcon className={styles.navIcon} />
                      Export All to Excel
                    </>
                  )}
                </button>
                <button
                  onClick={handlePrint}
                  className={styles.navButton}
                  style={{
                    background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <PrinterIcon className={styles.navIcon} />
                  Print All
                </button>
                <button
                  onClick={handleReset}
                  className={styles.navButton}
                >
                  <ArrowPathIcon className={styles.navIcon} />
                  Generate New
                </button>
              </div>
            </div>

            {/* Timesheets Display */}
            <div ref={timesheetPrintRef}>
              {generatedTimesheets.map((timesheet, index) => (
                <div key={index} className="timesheet-container" style={{
                  background: 'white',
                  borderRadius: '1rem',
                  padding: '2rem',
                  marginBottom: '2rem',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  {/* Header */}
                  <div style={{
                    textAlign: 'center',
                    marginBottom: '2rem',
                    borderBottom: '3px solid #c29e5d',
                    paddingBottom: '1rem'
                  }}>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: '#c29e5d',
                      marginBottom: '0.5rem'
                    }}>
                      PHOENICIAN TECHNICAL SERVICES LLC
                    </div>
                    <div style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#6b7280'
                    }}>
                      EMPLOYEE TIMESHEET
                    </div>
                  </div>

                  {/* Employee Info */}
                  <div style={{
                    background: '#f8f9fa',
                    padding: '1.5rem',
                    borderRadius: '0.5rem',
                    marginBottom: '2rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '1rem'
                  }}>
                    <div style={{ fontSize: '0.875rem' }}>
                      <span style={{ fontWeight: 'bold', color: '#374151' }}>Name: </span>
                      <span style={{ color: '#111827' }}>{timesheet.employee.name}</span>
                    </div>
                    <div style={{ fontSize: '0.875rem' }}>
                      <span style={{ fontWeight: 'bold', color: '#374151' }}>PIN/AUX: </span>
                      <span style={{ color: '#111827' }}>{timesheet.employee.pin}</span>
                    </div>
                    <div style={{ fontSize: '0.875rem' }}>
                      <span style={{ fontWeight: 'bold', color: '#374151' }}>Designation: </span>
                      <span style={{ color: '#111827' }}>{timesheet.employee.designation}</span>
                    </div>
                    <div style={{ fontSize: '0.875rem' }}>
                      <span style={{ fontWeight: 'bold', color: '#374151' }}>Department: </span>
                      <span style={{ color: '#111827' }}>{timesheet.employee.department}</span>
                    </div>
                    <div style={{ fontSize: '0.875rem', gridColumn: 'span 2' }}>
                      <span style={{ fontWeight: 'bold', color: '#374151' }}>Period: </span>
                      <span style={{ color: '#111827' }}>{getSelectedDateRange().label}</span>
                    </div>
                  </div>

                  {/* Attendance Table */}
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.875rem'
                    }}>
                      <thead>
                        <tr>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                            minWidth: '100px'
                          }}>Date</th>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'center',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                            minWidth: '80px'
                          }}>Check-In</th>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'center',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                            minWidth: '80px'
                          }}>Check-Out</th>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'center',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                            minWidth: '70px'
                          }}>Reg. Hours</th>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'center',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                            minWidth: '80px'
                          }}>Break</th>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'center',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                            minWidth: '70px'
                          }}>No-Break</th>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                            minWidth: '180px'
                          }}>Overtime</th>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'center',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                            minWidth: '70px'
                          }}>OT Hours</th>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'center',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                            minWidth: '80px'
                          }}>Total Hours</th>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                            minWidth: '200px'
                          }}>Remarks</th>
                          <th style={{
                            background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                            color: 'white',
                            padding: '0.75rem 0.5rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            minWidth: '200px'
                          }}>Discrepancy</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timesheet.records.map((record, idx) => (
                          <tr key={idx} style={{
                            background: idx % 2 === 0 ? 'white' : '#f9fafb',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              borderRight: '1px solid #e5e7eb',
                              fontWeight: '500',
                              color: '#111827'
                            }}>{record.dateString}</td>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              textAlign: 'center',
                              borderRight: '1px solid #e5e7eb',
                              color: record.checkIn ? '#111827' : '#9ca3af'
                            }}>{record.checkIn || '-'}</td>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              textAlign: 'center',
                              borderRight: '1px solid #e5e7eb',
                              color: record.checkOut ? '#111827' : '#9ca3af'
                            }}>{record.checkOut || '-'}</td>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              textAlign: 'center',
                              borderRight: '1px solid #e5e7eb',
                              fontWeight: '600',
                              color: '#111827'
                            }}>{record.regularHours}</td>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              textAlign: 'center',
                              borderRight: '1px solid #e5e7eb',
                              color: record.breakTime ? '#d97706' : '#9ca3af',
                              fontWeight: record.breakTime ? '600' : 'normal'
                            }}>{record.breakTime || '-'}</td>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              textAlign: 'center',
                              borderRight: '1px solid #e5e7eb',
                              background: record.noBreakCount > 0 ? '#fef3c7' : 'transparent',
                              color: record.noBreakCount > 0 ? '#d97706' : '#9ca3af',
                              fontWeight: record.noBreakCount > 0 ? '600' : 'normal'
                            }}>{record.noBreakCount}</td>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              borderRight: '1px solid #e5e7eb',
                              fontSize: '0.813rem',
                              color: '#374151'
                            }}>
                              {record.overtimeSessions.length > 0 ? (
                                record.overtimeSessions.map((ot, otIdx) => (
                                  <div key={otIdx} style={{ marginBottom: otIdx < record.overtimeSessions.length - 1 ? '0.25rem' : 0 }}>
                                    {ot.projectCode}: {ot.startTime}-{ot.endTime}
                                  </div>
                                ))
                              ) : (
                                <span style={{ color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              textAlign: 'center',
                              borderRight: '1px solid #e5e7eb',
                              fontWeight: record.overtimeHours > 0 ? '600' : 'normal',
                              color: record.overtimeHours > 0 ? '#059669' : '#9ca3af'
                            }}>{record.overtimeHours}</td>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              textAlign: 'center',
                              borderRight: '1px solid #e5e7eb',
                              fontWeight: '700',
                              fontSize: '0.938rem',
                              color: '#111827'
                            }}>{record.totalHours}</td>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              fontSize: '0.813rem',
                              borderRight: '1px solid #e5e7eb',
                              color: record.remarks ? '#374151' : '#9ca3af'
                            }}>{record.remarks || '-'}</td>
                            <td style={{
                              padding: '0.75rem 0.5rem',
                              fontSize: '0.813rem',
                              color: record.discrepancy ? '#dc2626' : '#9ca3af',
                              fontWeight: record.discrepancy ? '600' : 'normal'
                            }}>{record.discrepancy || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ViewTimesheetsPage;